/**
 * @fileoverview Tabela densa de usuários no padrão visual canônico do app
 * (mesmo usado em Empresas → Usuários). Componente compartilhado: a página
 * /users e o detalhe de Empresa renderizam exatamente a mesma UI a partir
 * daqui. Inclui também os helpers de busca e paginação no mesmo padrão.
 *
 * Não há um "segundo padrão" — qualquer ajuste visual de tabela de usuários
 * é feito aqui.
 *
 * Opcionais (a tela de Empresas usa): coluna "Último login" e ordenação por
 * coluna, clicando no título (`sort` + `onSortChange`, com `sortUsers` e
 * `nextUsersSort` para a lista).
 */

import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Tooltip } from "@material-ui/core";
import { formatRelativeTime } from "../../utils/formatRelativeTime";
import {
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  Eye,
  Pencil,
  Trash2,
  Search,
  ChevronRight,
  Sparkles,
  MessageCircle,
  Headphones,
  ShieldCheck,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers públicos — exportados pra consumidores que precisam dos mesmos
// avatares colorizados em outros lugares (ex: cards de detalhe).
// ─────────────────────────────────────────────────────────────────────────────
export const stringToColor = (str) => {
  if (!str) return "#9e9e9e";
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const colors = ["#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#06b6d4", "#6366f1", "#f43f5e"];
  return colors[Math.abs(hash) % colors.length];
};

export const initials = (name) => {
  if (!name) return "?";
  // Array.from: 1º caractere real (emoji/astral não vira meio-surrogate).
  return name.split(" ").filter(Boolean).slice(0, 2).map(s => Array.from(s)[0]).join("").toUpperCase();
};

const ACCESS_SCOPE_META = {
  full:       { label: "Completo",    color: "#5B7CFF", Icon: Sparkles },
  internal:   { label: "Chat Interno", color: "#10B981", Icon: MessageCircle },
  attendance: { label: "Atendimento",  color: "#F59E0B", Icon: Headphones },
};

// Largura de cada coluna no grid (a de Ações, 100px, vai no fim).
const COLUMN_WIDTH = {
  name: "minmax(200px, 1.4fr)",
  email: "minmax(180px, 1.3fr)",
  company: "minmax(140px, 0.9fr)",
  role: "110px",
  access: "minmax(130px, 0.8fr)",
  lastLogin: "minmax(130px, 0.8fr)",
};

// O que as células de Cargo, Acesso e Último login mostram (e a ordenação usa).
const roleLabel = (u) => (u.profile === "admin" ? "Admin" : "Usuário");
const accessMeta = (u) => (u.accessScope && ACCESS_SCOPE_META[u.accessScope]) || null;
// Data do último login, ou null (sem login registrado ou data inválida).
const lastLoginDate = (u) => {
  if (!u.lastLoginAt) return null;
  const d = new Date(u.lastLoginAt);
  return Number.isNaN(d.getTime()) ? null : d;
};

// "09/10/2026 14:32" na hora local.
const formatLoginDate = (d) =>
  d
    .toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    .replace(",", "");

// Valor de cada coluna para ordenar: o que a célula mostra.
const SORT_VALUE = {
  name: (u) => u.name,
  email: (u) => u.email,
  company: (u, getCompanyName) => (getCompanyName ? getCompanyName(u) : u.company?.name),
  role: roleLabel,
  access: (u) => accessMeta(u)?.label,
  lastLogin: (u) => lastLoginDate(u)?.getTime() ?? null,
};

const isEmpty = (v) => v == null || v === "";

/**
 * A lista ordenada pela coluna (`sort` = { key, dir }), sem mudar a original. Os
 * vazios (sem conta, sem login registrado...) ficam sempre no fim.
 */
export const sortUsers = (users, sort, getCompanyName) => {
  const valueFor = sort && SORT_VALUE[sort.key];
  if (!valueFor) return users;
  const direction = sort.dir === "desc" ? -1 : 1;
  return [...users].sort((a, b) => {
    const va = valueFor(a, getCompanyName);
    const vb = valueFor(b, getCompanyName);
    if (isEmpty(va) || isEmpty(vb)) {
      if (isEmpty(va) && isEmpty(vb)) return 0;
      return isEmpty(va) ? 1 : -1;
    }
    const result =
      typeof va === "number"
        ? va - vb
        : String(va).localeCompare(String(vb), "pt-BR", { sensitivity: "base", numeric: true });
    return result * direction;
  });
};

/**
 * Ordenação depois do clique no título: a mesma coluna inverte; outra começa em
 * ordem crescente (o último login, do mais recente).
 */
export const nextUsersSort = (sort, key) =>
  sort?.key === key
    ? { key, dir: sort.dir === "asc" ? "desc" : "asc" }
    : { key, dir: key === "lastLogin" ? "desc" : "asc" };

// ─────────────────────────────────────────────────────────────────────────────
// Styles — espelham CompaniesByGroup.userTable* / searchInput (manter em
// sincronia). A paginação é esta mesma, usada também em Empresas.
// ─────────────────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    // ── Tabela ──
    table: {
      borderRadius: 14,
      border: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.06)"}`,
      background: dark ? "rgba(255,255,255,0.018)" : "#fff",
      overflow: "hidden",
    },
    tableHeader: {
      display: "grid",
      gap: 14,
      padding: "14px 20px",
      borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)"}`,
      fontSize: 12,
      fontWeight: 500,
      color: dark ? "#71717A" : "#A1A1AA",
      [theme.breakpoints.down("md")]: { display: "none" },
    },
    tableRow: {
      display: "grid",
      gap: 14,
      padding: "12px 20px",
      borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)"}`,
      alignItems: "center",
      transition: "background .12s",
      "&:last-child": { borderBottom: "none" },
      "&:hover": { background: dark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" },
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr !important",
        gap: 6,
        padding: "14px 16px",
      },
    },
    nameCell: { display: "flex", alignItems: "center", gap: 12, minWidth: 0 },
    // Iniciais em tom neutro (sem cor por usuário), nos dois temas.
    avatar: {
      width: 32,
      height: 32,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 12,
      fontWeight: 700,
      flexShrink: 0,
      background: dark ? "rgba(255,255,255,0.06)" : "#F4F4F5",
      color: dark ? "#A1A1AA" : "#52525B",
      border: `1px solid ${dark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"}`,
    },
    avatarPhoto: { objectFit: "cover", padding: 0 },
    nameWrap: { minWidth: 0, display: "flex", flexDirection: "column", gap: 2 },
    name: {
      fontSize: 14,
      fontWeight: 600,
      color: dark ? "#FAFAFA" : "#09090B",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexWrap: "wrap",
    },
    selfTag: { fontSize: 10, fontWeight: 600, color: dark ? "#71717A" : "#A1A1AA" },
    inlineBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 3,
      padding: "1px 6px",
      borderRadius: 6,
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      flexShrink: 0,
    },
    aiBadge: {
      background: "linear-gradient(135deg, #5B7CFF 0%, #9D7CFF 100%)",
      color: "#fff",
    },
    mfaBadge: {
      background: "rgba(16,185,129,0.18)",
      color: "#10B981",
      border: `1px solid rgba(16,185,129,0.32)`,
    },
    cell: {
      fontSize: 13,
      color: dark ? "#A1A1AA" : "#52525B",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    roleBadge: {
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 12px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      border: "1px solid",
      justifySelf: "start",
    },
    scopeBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "3px 10px",
      borderRadius: 8,
      fontSize: 11,
      fontWeight: 600,
      border: "1px solid",
      whiteSpace: "nowrap",
    },
    actions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 4,
    },
    iconBtn: {
      width: 32,
      height: 32,
      border: "none",
      background: "transparent",
      borderRadius: 8,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: dark ? "#A1A1AA" : "#71717A",
      transition: "background 0.15s, color 0.15s",
      "&:hover:not(:disabled)": { background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" },
      "&:disabled": { opacity: 0.45, cursor: "not-allowed" },
    },
    iconBtnDanger: { "&:hover:not(:disabled)": { background: "rgba(239,68,68,0.1)", color: "#EF4444" } },

    // ── Título que ordena ──
    sortBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: 0,
      border: "none",
      background: "transparent",
      font: "inherit",
      color: "inherit",
      cursor: "pointer",
      justifySelf: "start",
      borderRadius: 4,
      "&:hover": { color: dark ? "#D4D4D8" : "#3F3F46" },
      "&:focus-visible": { outline: `2px solid ${dark ? "#5B7CFF" : "#3D5CFD"}`, outlineOffset: 2 },
    },
    sortBtnActive: { color: dark ? "#FAFAFA" : "#09090B" },
    sortIdle: { opacity: 0.45 },

    // ── Empty / skeleton ──
    emptyState: {
      textAlign: "center",
      padding: 48,
      color: dark ? "#71717A" : "#A1A1AA",
      fontSize: 14,
      background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
      borderRadius: 12,
      border: `1px dashed ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
    },
    skeletonRow: {
      display: "grid",
      gap: 14,
      padding: "12px 20px",
      borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)"}`,
      alignItems: "center",
      "&:last-child": { borderBottom: "none" },
    },
    skel: {
      background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
      borderRadius: 6,
      animation: "$pulse 1.4s ease-in-out infinite",
    },
    "@keyframes pulse": {
      "0%, 100%": { opacity: 1 },
      "50%": { opacity: 0.55 },
    },

    // ── Search ──
    searchWrap: { position: "relative", marginBottom: 16 },
    searchInput: {
      width: "100%",
      height: 44,
      paddingLeft: 44,
      paddingRight: 16,
      borderRadius: 12,
      background: dark ? "rgba(255,255,255,0.025)" : "#fff",
      border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.08)"}`,
      color: dark ? "#FAFAFA" : "#09090B",
      fontSize: 14,
      fontFamily: "inherit",
      outline: "none",
      boxSizing: "border-box",
      transition: "border-color .15s, background .15s, box-shadow .15s",
      "&:focus": {
        borderColor: dark ? "#5B7CFF" : "#3D5CFD",
        boxShadow: dark
          ? "0 0 0 1px rgba(91,124,255,0.15)"
          : "0 0 0 1px rgba(61,92,253,0.12)",
      },
      "&::placeholder": { color: dark ? "#71717A" : "#A1A1AA" },
    },
    searchIcon: {
      position: "absolute",
      left: 14,
      top: "50%",
      transform: "translateY(-50%)",
      color: dark ? "#71717A" : "#A1A1AA",
      pointerEvents: "none",
      display: "flex",
    },

    // ── Pagination ──
    pagWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      marginTop: 20,
    },
    pagBtn: {
      width: 36,
      height: 36,
      borderRadius: 10,
      border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "#d8dde6"}`,
      background: dark ? "rgba(255,255,255,0.025)" : "#fff",
      color: dark ? "#A1A1AA" : "#64748b",
      fontSize: 13,
      fontWeight: 600,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "background .15s, border-color .15s, color .15s",
      padding: 0,
      fontFamily: "inherit",
      "&:hover:not(:disabled)": {
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)",
        color: dark ? "#FAFAFA" : "#09090B",
      },
      "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
    },
    // O hover tem a mesma força do hover do pagBtn (e vem depois): a página atual
    // continua azul com o mouse em cima, em vez de virar o cinza dos outros botões.
    pagBtnActive: {
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#fff",
      borderColor: "transparent",
      "&:hover:not(:disabled)": {
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
        color: "#fff",
      },
    },
  };
});

// ─────────────────────────────────────────────────────────────────────────────
// Search input no padrão do app
// ─────────────────────────────────────────────────────────────────────────────
export const UsersTableSearch = ({
  value,
  onChange,
  placeholder = "Buscar usuário por nome ou email...",
  className,
}) => {
  const classes = useStyles();
  return (
    <div className={`${classes.searchWrap} ${className || ""}`}>
      <span className={classes.searchIcon}>
        <Search size={16} />
      </span>
      <input
        type="search"
        autoComplete="off"
        className={classes.searchInput}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Pagination no padrão do app
// ─────────────────────────────────────────────────────────────────────────────
export const UsersTablePagination = ({ current, total, onChange }) => {
  const classes = useStyles();
  if (total <= 1) return null;
  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, current - Math.floor(maxVisible / 2));
  let end = Math.min(total, start + maxVisible - 1);
  start = Math.max(1, Math.min(start, end - maxVisible + 1));
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className={classes.pagWrap}>
      <button
        className={classes.pagBtn}
        onClick={() => onChange(current - 1)}
        disabled={current === 1}
        aria-label="Página anterior"
      >
        <ChevronRight size={16} style={{ transform: "rotate(180deg)" }} />
      </button>
      {pages.map(p => (
        <button
          key={p}
          className={`${classes.pagBtn} ${p === current ? classes.pagBtnActive : ""}`}
          onClick={() => onChange(p)}
          aria-current={p === current ? "page" : undefined}
        >
          {p}
        </button>
      ))}
      <button
        className={classes.pagBtn}
        onClick={() => onChange(current + 1)}
        disabled={current === total}
        aria-label="Próxima página"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Tabela principal
// ─────────────────────────────────────────────────────────────────────────────
const UsersTable = ({
  users = [],
  loading = false,
  loadingRows = 8,
  currentUserId,
  showCompanyColumn = true,
  // Função opcional pra resolver o nome da Conta principal. Recebe o user e
  // retorna string ou null. Quando null → célula renderiza "—".
  getCompanyName,
  // Impersonar: super pode "acessar como" outro user. Esconder pra não-super
  // ou quando é o próprio user logado é tratado aqui.
  canImpersonate = false,
  impersonatingId = null,
  onImpersonate,
  // Ações
  onEdit,
  onDelete,
  // canDelete recebe o user e diz se ele pode ser excluído: a própria linha fica
  // com o botão desligado; as outras sem permissão, sem o botão.
  // Default: bloqueia auto-exclusão.
  canDelete,
  // canEdit recebe o user e retorna se o botão de editar aparece (padrão: sempre).
  canEdit,
  // Empty states
  searchActive = false,
  emptyMessage = "Nenhum usuário cadastrado.",
  emptySearchMessage = "Nenhum usuário encontrado.",
  // Coluna "Último login" (data e hora; vazio = nenhum login registrado).
  showLastLogin = false,
  // Ordenação: { key, dir } e o clique no título (sem onSortChange, títulos fixos).
  sort = null,
  onSortChange,
}) => {
  const classes = useStyles();

  // Colunas: Nome | Email | [Conta principal] | Cargo | Acesso | [Último login] | Ações.
  // O grid template fica inline porque depende de prop runtime e o makeStyles
  // não suporta variantes baseadas em prop sem `props =>`. Mantém legível.
  const columns = [
    { key: "name", label: "Nome" },
    { key: "email", label: "Email" },
    showCompanyColumn && { key: "company", label: "Conta principal" },
    { key: "role", label: "Cargo" },
    { key: "access", label: "Acesso" },
    showLastLogin && { key: "lastLogin", label: "Último login" },
  ].filter(Boolean);
  const gridTemplate = `${columns.map((c) => COLUMN_WIDTH[c.key]).join(" ")} 100px`;

  const sortIcon = (key) => {
    if (sort?.key !== key) return <ChevronsUpDown size={12} className={classes.sortIdle} aria-hidden="true" />;
    return sort.dir === "asc" ? <ArrowUp size={12} aria-hidden="true" /> : <ArrowDown size={12} aria-hidden="true" />;
  };

  const renderHeader = () => (
    <div className={classes.tableHeader} style={{ gridTemplateColumns: gridTemplate }}>
      {columns.map((c) =>
        onSortChange ? (
          <button
            key={c.key}
            type="button"
            className={`${classes.sortBtn} ${sort?.key === c.key ? classes.sortBtnActive : ""}`}
            onClick={() => onSortChange(c.key)}
            aria-label={`Ordenar por ${c.label}${sort?.key === c.key ? (sort.dir === "asc" ? " (crescente)" : " (decrescente)") : ""}`}
          >
            {c.label}
            {sortIcon(c.key)}
          </button>
        ) : (
          <div key={c.key}>{c.label}</div>
        )
      )}
      <div style={{ textAlign: "right" }}>Ações</div>
    </div>
  );

  const renderSkeleton = () => (
    <div className={classes.table}>
      {renderHeader()}
      {Array.from({ length: loadingRows }).map((_, i) => (
        <div key={i} className={classes.skeletonRow} style={{ gridTemplateColumns: gridTemplate }}>
          <div className={classes.nameCell}>
            <div className={classes.skel} style={{ width: 32, height: 32, borderRadius: "50%" }} />
            <div className={classes.skel} style={{ width: 120, height: 14 }} />
          </div>
          <div className={classes.skel} style={{ width: "70%", height: 12 }} />
          {showCompanyColumn && <div className={classes.skel} style={{ width: "60%", height: 12 }} />}
          <div className={classes.skel} style={{ width: 72, height: 22, borderRadius: 999 }} />
          <div className={classes.skel} style={{ width: 100, height: 22, borderRadius: 6 }} />
          {showLastLogin && <div className={classes.skel} style={{ width: "70%", height: 12 }} />}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 4 }}>
            <div className={classes.skel} style={{ width: 32, height: 32, borderRadius: 8 }} />
            <div className={classes.skel} style={{ width: 32, height: 32, borderRadius: 8 }} />
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) return renderSkeleton();

  if (users.length === 0) {
    return (
      <div className={classes.emptyState}>
        {searchActive ? emptySearchMessage : emptyMessage}
      </div>
    );
  }

  return (
    <div className={classes.table}>
      {renderHeader()}
      {users.map((u) => {
        const isAdmin = u.profile === "admin";
        const isSelf = currentUserId != null && currentUserId === u.id;
        const companyName = showCompanyColumn
          ? (getCompanyName ? getCompanyName(u) : (u.company?.name || null))
          : null;
        const deletable = canDelete ? canDelete(u) : !isSelf;
        const scope = accessMeta(u);
        const lastLogin = showLastLogin ? lastLoginDate(u) : null;

        return (
          <div key={u.id} className={classes.tableRow} style={{ gridTemplateColumns: gridTemplate }}>
            <div className={classes.nameCell}>
              {u.profileImage ? (
                <img src={u.profileImage} alt="" className={`${classes.avatar} ${classes.avatarPhoto}`} />
              ) : (
                <div className={classes.avatar}>{initials(u.name)}</div>
              )}
              <div className={classes.nameWrap}>
                <div className={classes.name} title={u.name}>
                  <span style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  }}>{u.name}</span>
                  {isSelf && <span className={classes.selfTag}>(você)</span>}
                  {u.isAI && (
                    <span className={`${classes.inlineBadge} ${classes.aiBadge}`}>
                      <Sparkles size={9} strokeWidth={2.5} />
                      IA
                    </span>
                  )}
                  {u.mfaEnabled && (
                    <span className={`${classes.inlineBadge} ${classes.mfaBadge}`} title="MFA ativado">
                      <ShieldCheck size={9} strokeWidth={2.5} />
                      MFA
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className={classes.cell} title={u.email}>{u.email || "—"}</div>

            {showCompanyColumn && (
              <div className={classes.cell} title={companyName || ""}>{companyName || "—"}</div>
            )}

            <div>
              <span
                className={classes.roleBadge}
                style={{
                  background: isAdmin
                    ? "rgba(91,124,255,0.14)"
                    : "rgba(255,255,255,0.04)",
                  color: isAdmin ? "#7C9BFF" : "#A1A1AA",
                  borderColor: isAdmin
                    ? "rgba(91,124,255,0.30)"
                    : "rgba(255,255,255,0.06)",
                }}
              >
                {roleLabel(u)}
              </span>
            </div>

            <div>
              {scope ? (
                <span
                  className={classes.scopeBadge}
                  style={{
                    background: `${scope.color}26`,
                    color: scope.color,
                    borderColor: `${scope.color}40`,
                  }}
                >
                  <scope.Icon size={11} strokeWidth={2.5} />
                  {scope.label}
                </span>
              ) : (
                <span className={classes.cell} style={{ opacity: 0.5 }}>—</span>
              )}
            </div>

            {showLastLogin && (
              <div className={classes.cell} title={lastLogin ? formatRelativeTime(lastLogin) : "Nenhum login registrado"}>
                {lastLogin ? formatLoginDate(lastLogin) : "—"}
              </div>
            )}

            <div className={classes.actions}>
              {canImpersonate && !isSelf && onImpersonate && (
                <Tooltip title={`Acessar como ${u.name}`} arrow placement="top">
                  <button
                    type="button"
                    className={classes.iconBtn}
                    onClick={() => onImpersonate(u)}
                    disabled={impersonatingId === u.id}
                    aria-label={`Acessar como ${u.name}`}
                  >
                    <Eye size={14} strokeWidth={2} />
                  </button>
                </Tooltip>
              )}
              {onEdit && (!canEdit || canEdit(u)) && (
                <button
                  type="button"
                  className={classes.iconBtn}
                  onClick={() => onEdit(u)}
                  title="Editar"
                  aria-label={`Editar ${u.name}`}
                >
                  <Pencil size={14} />
                </button>
              )}
              {onDelete && (deletable || isSelf) && (
                <button
                  type="button"
                  className={`${classes.iconBtn} ${classes.iconBtnDanger}`}
                  onClick={() => onDelete(u)}
                  disabled={!deletable}
                  title={deletable ? "Excluir" : "Você não pode excluir a si mesmo"}
                  aria-label={`Excluir ${u.name}`}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default UsersTable;
